import React from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Menu, X, UserRound, HelpCircle, DollarSign, LogOut } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const Header = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [showUserMenu, setShowUserMenu] = React.useState(false);
  const userMenuRef = React.useRef(null);

  const handleLogout = () => {
    logout();
    navigate('/');
    setShowUserMenu(false);
  };

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navLinkClasses = ({ isActive }) =>
    `text-sm font-medium transition-colors ${
      isActive ? 'text-purple-600' : 'text-gray-700 hover:text-purple-600'
    }`;

  const mobileNavClasses = ({ isActive }) =>
    `block px-4 py-2 rounded-md transition-colors ${
      isActive ? 'bg-purple-100 text-purple-700' : 'text-gray-700 hover:bg-purple-50'
    }`;

  return (
    <header className="bg-white/90 backdrop-blur-sm border-b border-purple-100 sticky top-0 z-30">
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2">
            <span className="text-2xl font-black tracking-tight leading-none select-none">
              <span className="text-[#ff405c]">my</span>
              <span className="text-[#5c3fd8]">STORY</span>
              <span className="text-[#ff405c]">me</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center space-x-6 flex-1 justify-center">
            <NavLink to="/" className={navLinkClasses} end>
              Home
            </NavLink>
            <NavLink to="/about" className={navLinkClasses}>
              About Us
            </NavLink>
            <NavLink to="/help" className={navLinkClasses}>
              Help Center
            </NavLink>
            <NavLink to="/pricing" className={navLinkClasses}>
              Pricing
            </NavLink>
            {isAuthenticated && (
              <>
                <NavLink to="/profiles" className={navLinkClasses}>
                  Profiles
                </NavLink>
                <NavLink to="/stories" className={navLinkClasses}>
                  Stories
                </NavLink>
              </>
            )}
          </nav>

          <div className="hidden md:flex items-center space-x-4">
            {isAuthenticated ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setShowUserMenu((prev) => !prev)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full border transition-all shadow-sm ${
                    showUserMenu
                      ? 'border-purple-400 bg-purple-50 text-purple-700'
                      : 'border-purple-200 bg-white text-purple-600 hover:border-purple-400'
                  }`}
                >
                  <Menu className="h-4 w-4" />
                  <div className="w-7 h-7 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white flex items-center justify-center text-sm font-semibold">
                    {(user?.firstName?.[0] || user?.email?.[0] || 'U').toUpperCase()}
                  </div>
                </button>
                {showUserMenu && (
                  <div className="absolute right-0 mt-3 w-52 bg-white rounded-2xl shadow-2xl border border-purple-50 py-3 text-sm text-purple-700">
                    <p className="px-5 pb-2 text-xs uppercase tracking-[0.3em] text-purple-400">
                      {user?.firstName ? `Hi, ${user.firstName}` : 'Account'}
                    </p>
                    <NavLink
                      to="/account"
                      className="flex items-center gap-3 px-5 py-2 hover:bg-purple-50 transition-colors"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <UserRound className="h-4 w-4" />
                      My Account
                    </NavLink>
                    <NavLink
                      to="/help"
                      className="flex items-center gap-3 px-5 py-2 hover:bg-purple-50 transition-colors"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <HelpCircle className="h-4 w-4" />
                      Help
                    </NavLink>
                    <NavLink
                      to="/pricing"
                      className="flex items-center gap-3 px-5 py-2 hover:bg-purple-50 transition-colors"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <DollarSign className="h-4 w-4" />
                      Pricing
                    </NavLink>
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-3 w-full text-left px-5 py-2 hover:bg-purple-50 text-red-500 transition-colors"
                    >
                      <LogOut className="h-4 w-4" />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <NavLink to="/login" className={navLinkClasses}>
                  Sign In
                </NavLink>
                <Link
                  to="/register"
                  className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded-md font-medium hover:from-purple-700 hover:to-pink-700 transition-all"
                >
                  Sign Up
                </Link>
              </>
            )}
          </div>

          {/* Mobile Sign In/Sign Up Buttons - Only show when not authenticated */}
          {!isAuthenticated && (
            <div className="md:hidden flex items-center gap-2 mr-2">
              <Link
                to="/login"
                className="text-sm font-medium text-purple-600 hover:text-purple-700 px-3 py-1.5 transition-colors"
              >
                Sign In
              </Link>
              <Link
                to="/register"
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-medium px-3 py-1.5 rounded-md hover:from-purple-700 hover:to-pink-700 transition-all"
              >
                Sign Up
              </Link>
            </div>
          )}

          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden mt-4 space-y-2">
            <NavLink
              to="/"
              className={mobileNavClasses}
              end
              onClick={() => setMobileMenuOpen(false)}
            >
              Home
            </NavLink>
            <NavLink
              to="/about"
              className={mobileNavClasses}
              onClick={() => setMobileMenuOpen(false)}
            >
              About Us
            </NavLink>
            <NavLink
              to="/help"
              className={mobileNavClasses}
              onClick={() => setMobileMenuOpen(false)}
            >
              Help Center
            </NavLink>
            {isAuthenticated ? (
              <>
                <NavLink
                  to="/profiles"
                  className={mobileNavClasses}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Profiles
                </NavLink>
                <NavLink
                  to="/stories"
                  className={mobileNavClasses}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Stories
                </NavLink>
                <NavLink
                  to="/account"
                  className={mobileNavClasses}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <div className="flex items-center gap-2">
                    <UserRound className="h-4 w-4" />
                    My Account
                  </div>
                </NavLink>
                <NavLink
                  to="/pricing"
                  className={mobileNavClasses}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Pricing
                </NavLink>
                <button
                  onClick={() => {
                    handleLogout();
                    setMobileMenuOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-purple-50 rounded-md"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <NavLink
                  to="/login"
                  className={mobileNavClasses}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Sign In
                </NavLink>
                <Link
                  to="/register"
                  className="block px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-md"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Sign Up
                </Link>
              </>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;

